import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlarmClock, FolderKanban, CalendarClock } from "lucide-react";
import api from "../lib/api";
import { formatDate } from "../lib/format";
import { toTs } from "../pages/studio/Agenda";

const DAY_MS = 86400000;
const GIORNI = 7;

function urgency(dayDiff) {
  if (dayDiff < 0) return { label: `Scaduto da ${-dayDiff}g`, cls: "bg-red-500/15 text-red-300 border-red-500/40", card: "border-l-red-500" };
  if (dayDiff === 0) return { label: "Oggi", cls: "bg-red-500/15 text-red-300 border-red-500/40", card: "border-l-red-500" };
  if (dayDiff === 1) return { label: "Domani", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40", card: "border-l-amber-500" };
  return { label: `Tra ${dayDiff} giorni`, cls: "bg-sky-500/15 text-sky-300 border-sky-500/40", card: "border-l-sky-500" };
}

export default function ScadenzeImminenti({ onCount }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/studio/agenda").then(({ data }) => {
      if (!alive) return;
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const all = [
        ...(data.promemoria || []).map((p) => ({ ...p, _type: "promemoria", _ts: toTs(p.data) })),
        ...(data.appuntamenti || []).map((a) => ({ ...a, _type: "appuntamento", _ts: toTs(a.data, a.ora) })),
      ]
        .filter((it) => Number.isFinite(it._ts))
        .map((it) => {
          const d = new Date(it._ts); d.setHours(0, 0, 0, 0);
          return { ...it, _dayDiff: Math.round((d.getTime() - today.getTime()) / DAY_MS) };
        })
        .filter((it) => it._dayDiff <= GIORNI)
        .sort((a, b) => a._ts - b._ts);
      setItems(all);
      onCount && onCount(all.length);
    }).catch(() => {});
    return () => { alive = false; };
  }, [onCount]);

  if (items.length === 0) return null;

  const go = (it) => {
    const pid = it._type === "promemoria" ? it.pratica_id : it.pratica_id;
    if (pid) navigate(`/app/pratiche?p=${pid}`);
    else navigate("/app/agenda");
  };

  return (
    <div className="pgp-card p-5 mt-6 pgp-fade-up border border-amber-500/30" data-testid="scadenze-imminenti">
      <div className="flex items-center gap-2 mb-4">
        <AlarmClock className="h-5 w-5 text-amber-400" />
        <span className="font-head font-semibold text-slate-100">Scadenze imminenti</span>
        <span className="text-xs text-slate-500">· prossimi {GIORNI} giorni</span>
        <span className="ml-auto text-xs font-mono bg-amber-500 text-[#0a0d14] rounded-full px-2 py-0.5 font-semibold" data-testid="scadenze-count">{items.length}</span>
      </div>
      <div className="space-y-2" data-testid="scadenze-list">
        {items.map((it) => {
          const u = urgency(it._dayDiff);
          const isProm = it._type === "promemoria";
          const clickable = isProm ? !!it.pratica_id : !!it.pratica_id;
          return (
            <button key={`${it._type}-${it.id}`} type="button" onClick={() => go(it)}
              data-testid={`scadenza-${it._type}-${it.id}`}
              className={`w-full text-left flex items-start gap-3 rounded-md bg-slate-900/40 border-l-4 ${u.card} p-3 hover:bg-slate-800/50 transition-colors ${clickable ? "" : "cursor-default"}`}>
              {isProm ? <FolderKanban className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" /> : <CalendarClock className="h-4 w-4 text-sky-400 shrink-0 mt-0.5" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-wider font-mono rounded px-1.5 py-0.5 border ${u.cls}`}>{u.label}</span>
                  {(it.numero_pratica) && <span className="text-xs font-mono text-slate-300" data-testid={`scadenza-pratica-${it.id}`}>{it.numero_pratica}{it.cliente ? <span className="font-sans"> - {it.cliente}</span> : null}</span>}
                </div>
                <div className="text-sm text-slate-100 mt-1 truncate">{isProm ? (it.descrizione || "Promemoria") : (it.titolo || "Appuntamento")}</div>
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-300 font-mono shrink-0">
                <CalendarClock className="h-3.5 w-3.5" /> {formatDate(it.data)}{!isProm && it.ora ? ` ${it.ora}` : ""}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
